import { useEffect, useState, useRef } from "react";

export default function Question({ question, next, question_number, setScore }) {
  const [response, setResponse] = useState([]);
  const [timer, setTimer] = useState(18);
  const [selected, setSelected] = useState(null);
  const [good, setGood] = useState(false);
  const [pointsMessage, setPointsMessage] = useState("");
  const [showTips, setShowTips] = useState(false);
  const [answered, setAnswered] = useState(false);
  const [timerExpired, setTimerExpired] = useState(false);
  const [attemptedAnswers, setAttemptedAnswers] = useState([]);
  const intervalRef = useRef(null);
  const pointsTimeoutRef = useRef(null); // For tracking points message timeout

  useEffect(() => {
    // Only start the timer if not answered and timer hasn't expired and timer is positive
    if (!answered && !timerExpired && timer > 0) {
      intervalRef.current = setInterval(() => {
        setTimer((prev) => {
          // Check if we should stop the timer
          if (prev <= 0) {
            clearInterval(intervalRef.current);
            setTimerExpired(true);
            // No point deduction when time runs out
            setPointsMessage("Time's up!");
            displayPointsAndProceed();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      // Clean up points message timeout too
      if (pointsTimeoutRef.current) {
        clearTimeout(pointsTimeoutRef.current);
      }
    };
  }, [answered, timerExpired]);

  useEffect(() => {
    setResponse(shuffleArray([...question.responses]));
  }, [question]);

  useEffect(() => {
    const audio = new Audio('./assets/sounds/reveal-question.mp3');
    audio.play();
  }, []);

  const displayPointsAndProceed = () => {
    // Make sure we wait longer before showing tips
    setTimeout(() => {
      setShowTips(true);
    }, 2000);
  };

  const handleContinueAfterTips = () => {
    setTimeout(() => {
      next();
    }, 500);
  };

  const handleAnswerClick = (response, index) => {
    // If already answered correctly or this answer has been tried, ignore the click
    if (answered || attemptedAnswers.includes(index)) return;
    
    if (response.is_correct) {
      // Correct answer logic
      const audio = new Audio('./assets/sounds/true.mp3');
      audio.play();
      const pointsEarned = (timer * 5);
      setScore((prev) => prev + pointsEarned);
      setPointsMessage(`+ ${pointsEarned} PTS`);
      setGood(true);
      setSelected(index);
      
      // Now set answered to true, stop the timer and proceed
      setAnswered(true);
      clearInterval(intervalRef.current);
      displayPointsAndProceed();
    } else {
      // Incorrect answer logic
      const audio = new Audio('./assets/sounds/false.mp3');
      audio.play();
      setScore((prev) => (prev - 25 <= 0 ? 0 : prev - 25));
      setPointsMessage("- 25 PTS");
      
      // Add this answer to attempted answers
      setAttemptedAnswers(prev => [...prev, index]);
      
      // Temporarily set selected for this wrong answer
      setSelected(index);
      
      // Clear previous timeout if any
      if (pointsTimeoutRef.current) {
        clearTimeout(pointsTimeoutRef.current);
      }
      
      // Show the message longer, then clear it
      pointsTimeoutRef.current = setTimeout(() => {
        setPointsMessage("");
        setSelected(null); // Clear the wrong selection to allow new attempts
      }, 1500);
    }
  };

  // Calculate question font size based on length
  const getQuestionFontSize = () => {
    if (!question.title) return '1.5rem';
    const length = question.title.length;
    if (length > 100) return '1rem';
    if (length > 50) return '1.1rem';
    if (length > 25) return '1.3rem';
    return '1.2rem';
  };

  return (
    // Container div centered in the screen
    <div className="question-container" style={{
      position: 'fixed',
      width: '100%',
      height: '100%',
      left: '0',
      top: '5%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 100
    }}>
      {/* Question card with mobile-optimized sizing */}
      <div className="question-card" style={{
        transition: 'transform 250ms ease-in-out', // Smooth animation for all properties
        transform: 'scale(1.05)', // Slight grow effect
        transformOrigin: 'center',
        color: '#004A7C',
        width: '85%',
        maxWidth: '400px',
        height: '80%',
        maxHeight: '600px',
        backgroundColor: '#EFF3F8',
        borderRadius: '1em',
        boxShadow: '8px 10px 12px rgba(0,0,0,0.4)', // Slightly smaller shadow
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative'
      }}>
        <div className="question-content" style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
            padding: '0.75rem'  // Smaller padding for mobile
          }}>
          {!showTips ? (
            <>
            {/* Merged intro header: logo + city-specific subtitle */}
            <div className="question-intro-header" style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: '0 5%',
              marginBottom: '0.25rem',
              overflow: 'hidden'
            }}>
              <img
                style={{
                  width: '22%',
                  objectFit: 'contain',
                  marginBottom: '0.25rem'
                }}
                src="./assets/logo_question.png"
                alt="Question Logo"
              />
              <p style={{
                textAlign: 'center',
                margin: 0,
                fontSize: '0.85rem',
                lineHeight: '1.1rem'
              }}>
                {(() => {
                  switch (question.city?.toLowerCase()) {
                    case 'paris':
                      return 'Start your adventure in Paris by answering this first question.';
                    case 'budapest':
                      return 'You are in Budapest, a city of grand architecture and thermal wonders. But do you know all its secrets?';
                    case 'rome':
                      return 'Welcome to Rome, the Eternal City. Can you uncover its ancient mysteries?';
                    case 'barcelona':
                      return "You are in Barcelona, where Gaudí's art and Mediterranean charm meet. But do you know all its secrets?";
                    case 'oslo':
                      return 'You are in Oslo, a city rich in history and Nordic legends. But do you know all its secrets?';
                    case 'lisbon':
                      return 'You are in Lisbon, a city of explorers and sun-kissed hills. But do you know all its secrets?';
                    case 'amsterdam':
                      return 'You are in Amsterdam, a city of canals and cultural treasures. But do you know all its secrets?';
                    case 'athens':
                      return 'You are in Athens, the cradle of democracy and ancient myths. But do you know all its secrets?';
                    case 'london':
                        return 'You are in London, a city of royalty and hidden stories. But do you know all its secrets?';
                    case 'berlin':
                        return 'Discover Berlin, a city of history, culture, and modern vibrance!';
                    default:
                      return 'Welcome to a new adventure! Where will your journey take you?';
                  }
                })()}
              </p>
            </div>
            {/* Question area with fixed height */}
            <div className="question-title-area" style={{
              height: '22%',       // Smaller proportion for question
              minHeight: '40px',   // Smaller minimum height
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0.25rem',  // Smaller padding
              marginBottom: '0.5rem',
              overflow: 'auto'
            }}>
              <h1 style={{
                fontSize: getQuestionFontSize(),
                textAlign: 'center',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                margin: 0
              }}>
                {question.title}
              </h1>
            </div>
            
            {/* Answers container with fixed heights */}
            <div className="answers-container" style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              flex: 1,
              gap: '0.6rem',    // Smaller gap for mobile
              marginBottom: '0.75rem'
            }}>
              {response.map((resp, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswerClick(resp, index)}
                  style={{
                    flex: 1,
                    padding: '0.5rem',  // Smaller padding
                    margin: 'auto 0.5rem',
                    backgroundColor: 
                      // First, if this is the correct answer and it's been selected, always show green
                      (selected === index && good) ? '#72BF44' :
                      // Next, if this is selected but not correct
                      (selected === index) ? '#CF1B49' :
                      // If this was previously attempted (and must be wrong)
                      attemptedAnswers.includes(index) ? '#ffcccc' :
                      // Default case
                      'white',
                    borderRadius: '2rem',
                    cursor: answered ? 'default' : 'pointer', // No pointer cursor after answering correctly
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    color: selected === index ? 'white' : '#004A7C',
                    border: 'none',
                    outline: 'none',
                    fontSize: '0.9rem',  // Fixed smaller font size for mobile
                    lineHeight: '1.2',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {resp.title}
                </button>
              ))}
            </div>
            
            {/* Points display area */}
            <div className="points-display" style={{
              height: '10%',  // Smaller height
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontSize: '1.5rem',  // Smaller font
              padding: '0.5rem',
              marginBottom: '1rem',
              color: pointsMessage.includes('+') ? '#72BF44' : '#CF1B49'
            }}>
              {pointsMessage}
            </div>
            
            {/* Timer bar at the bottom */}
            <TimerBar time={timer} />
            </>
          ):(
            <>
            <div className="question-title-area" style={{
              height: '60%',       // Smaller proportion for question
              minHeight: '50px',   // Smaller minimum height
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '0.25rem',  // Smaller padding
              marginBottom: '0.5rem',
              marginTop:'1rem',
              overflow: 'auto',
            }}>
              <h1 style={{
                fontSize: '1.2rem',
                fontWeight: '400',
                textAlign: 'center',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                margin: 0
              }}>
                {question.tip || "'Did you know that the Eiffel Tower, originally planned to be dismantled after the 1889 World's Fair, was saved thanks to its use as a radio broadcasting antenna? Gustave Eiffel was able to extend his iconic structure well beyond the initially intended 20 years.'"}
              </h1>
            </div>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              marginTop: 'auto'
            }}>
              <img 
                src="./assets/GBPresenter.png" 
                alt="Tip Icon" 
                style={{ width: 'auto', height: '20vh', marginBottom: '0.75rem' }} 
              />
              
              <button 
                onClick={handleContinueAfterTips}
                style={{
                  boxShadow: '5px 8px 15px rgba(0,0,0,0.2)',
                  textAlign: "center",
                  color: '#004A7C',
                  backgroundColor: 'white',
                  borderRadius: '2em',
                  padding: '0.5rem 1.5rem',
                  marginBottom:'1rem',
                  border: 'none',
                  width: '80%',
                  cursor: "pointer",
                  textTransform:'uppercase'
                }}
              >
                Tap to continue
              </button>
            </div>
            </>

          )}
          </div>
      </div>
    </div>
  );
}

function TimerBar({ time }) {
  return (
    <div
      style={{
        position: 'absolute',
        bottom: '0.75rem',  // Smaller margin
        left: '10%',
        width: '80%',
        height: '10px',      // Slightly thinner
        backgroundColor: 'white',
        zIndex: 2000,
        borderRadius: '1em',
      }}
    >
      <div
        style={{
          width: `${(time / 18) * 100}%`,
          height: '100%',
          backgroundColor: '#004A7C',
          transition: 'width 1s linear',
          borderRadius: '1em',
        }}
      ></div>
    </div>
  );
}

function shuffleArray(array) {
  for (let i = array.length - 1; i > 0; i--) {
    let j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}